import React, { FC, Fragment, useEffect, useLayoutEffect } from 'react'
import { Redirect, Route, useHistory } from 'react-router'
import { CircularProgress } from '@mui/material'
import Box from '@mui/material/Box'
import dayjs from 'dayjs'

import api from '../../api'
import { AppContainer } from './styled'
import {
	selectAuthStatus,
	selectSkipMFAFlag,
} from '../../store/reducers/auth/selectors'
import { IRouteProps } from '../../types/routes'
import { AsyncFunction } from '../../types/common'
import { executeResponseError } from '../../helpers'
import { getUser } from '../../store/reducers/user/actions'
import { useTypedSelector } from '../../hooks/useTypedSelector'
import { selectUserId } from '../../store/reducers/user/selectors'
import { setSkipMfaAuth } from '../../store/reducers/auth/actions'
import { PAGE_LOGIN, PAGE_LOGIN_TERMS } from '../../constants/navigation'
import { useDefaultComponentTools } from '../../hooks/useDefaultComponentTools'

const PrivateRoute: FC<IRouteProps> = ({
	component: Component,
	...rest
}: IRouteProps) => {
	const { loading, setLoading, isMounted, dispatch, alert } =
		useDefaultComponentTools()
	const isAuth = useTypedSelector(selectAuthStatus)
	const userId = useTypedSelector(selectUserId)
	const skipMFAFlag = useTypedSelector(selectSkipMFAFlag)
	const history = useHistory()

	useLayoutEffect(() => {
		const getUserAsync: AsyncFunction = async () => {
			try {
				setLoading(true)
				const user = await dispatch(getUser())

				if (user && !user?.termsOfService?.approved) {
					history.replace(PAGE_LOGIN_TERMS)
				}
			} catch (e: any) {
				'message' in e && alert.error(e.message)
			} finally {
				isMounted() && setLoading(false)
			}
		}

		if (isAuth && !userId) {
			getUserAsync()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuth, userId, isMounted, dispatch])

	useEffect(() => {
		if (!userId || skipMFAFlag) return

		const sendReqForSkippingMfaForNextMonth = async (): Promise<void> => {
			try {
				const skipUntil = dayjs
					.utc(dayjs().add(1, 'month').startOf('day'))
					.format()

				await api.auth.skipMfaUntil(skipUntil, userId)
			} catch (e: any) {
				const error = executeResponseError(
					e,
					'Skip MFA for the next month error'
				)

				error && alert.error(error)
			} finally {
				dispatch(setSkipMfaAuth(true))
			}
		}

		sendReqForSkippingMfaForNextMonth()

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId, skipMFAFlag])

	if (!isAuth)
		return (
			<Redirect
				to={{
					pathname: PAGE_LOGIN,
				}}
			/>
		)

	return (
		<Fragment>
			{loading && (
				<Box
					display={'flex'}
					alignItems={'center'}
					justifyContent={'center'}
					position={'fixed'}
					top={0}
					right={0}
					bottom={0}
					left={0}
				>
					<CircularProgress />
				</Box>
			)}
			{!loading && userId && (
				<AppContainer>
					<Route
						{...rest}
						render={props => <Component {...props} />}
					/>
				</AppContainer>
			)}
		</Fragment>
	)
}

export default PrivateRoute
